/**
 * Ampliar imágenes al hacer clic.
 *
 * En una tienda de un solo producto la gente quiere mirar de cerca: el broche,
 * el grabado, el terminal. Sin esto la única forma de ver detalle es abrir la
 * imagen en otra pestaña.
 *
 * Se apoya en <dialog> nativo, que ya resuelve la capa superior, el fondo, el
 * cierre con Escape y dejar inerte el resto de la página. Reimplementar eso a
 * mano es de dónde salen los modales que atrapan el foco.
 *
 * Uso: marcar el contenedor con data-zoom. Dos contenedores con el mismo valor
 * (data-zoom="clasp") forman una galería por la que se navega con las flechas.
 */

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
const MAX = 5;

class Lightbox {
  #dlg; #fig; #img; #cap; #count; #prev; #next;
  #group = []; #i = 0; #origin = null;
  #scale = 1; #x = 0; #y = 0;
  #drag = null;
  #points = new Map(); #pinch = null;
  #closing = false;

  constructor() { this.#build(); }

  #build() {
    const d = document.createElement('dialog');
    d.className = 'lightbox';
    d.innerHTML = `
      <button class="lightbox__close" type="button" aria-label="Close">&times;</button>
      <button class="lightbox__nav lightbox__nav--prev" type="button" aria-label="Previous image">&#8592;</button>
      <button class="lightbox__nav lightbox__nav--next" type="button" aria-label="Next image">&#8594;</button>
      <figure class="lightbox__fig"></figure>
      <figcaption class="lightbox__cap"></figcaption>
      <div class="lightbox__meta">
        <p class="lightbox__count" aria-live="polite"></p>
        <p class="lightbox__hint">
          <span class="hint--fine">Scroll or click to zoom &middot; Esc to close</span>
          <span class="hint--coarse">Pinch or tap to zoom</span>
        </p>
      </div>`;
    document.body.append(d);

    this.#dlg = d;
    this.#fig = d.querySelector('.lightbox__fig');

    // La imagen se crea aquí pero no se cuelga del documento hasta la primera
    // apertura: un <img> sin src es HTML inválido y aparece como imagen rota en
    // cualquier auditoría de la página, aunque nunca llegue a pintarse.
    this.#img = document.createElement('img');
    this.#img.alt = '';
    this.#img.draggable = false;
    this.#cap = d.querySelector('.lightbox__cap');
    this.#count = d.querySelector('.lightbox__count');
    this.#prev = d.querySelector('.lightbox__nav--prev');
    this.#next = d.querySelector('.lightbox__nav--next');

    d.querySelector('.lightbox__close').addEventListener('click', () => this.close());
    this.#prev.addEventListener('click', () => this.go(-1));
    this.#next.addEventListener('click', () => this.go(1));

    // clic en el fondo, no en la imagen
    d.addEventListener('click', (e) => { if (e.target === d) this.close(); });
    d.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });

    d.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); }
      else if (e.key === '+' || e.key === '=') { e.preventDefault(); this.#zoomCentre(this.#scale * 1.4); }
      else if (e.key === '-') { e.preventDefault(); this.#zoomCentre(this.#scale / 1.4); }
      else if (e.key === '0') { e.preventDefault(); this.#reset(); this.#apply(); }
    });

    this.#img.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#zoomAt(this.#scale > 1 ? 1 : 2.4, e.clientX, e.clientY);
    });
    this.#img.addEventListener('dblclick', (e) => e.preventDefault());
    this.#img.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.#zoomAt(this.#scale * (e.deltaY < 0 ? 1.18 : 1 / 1.18), e.clientX, e.clientY);
    }, { passive: false });

    this.#img.addEventListener('pointerdown', (e) => this.#down(e));
    this.#img.addEventListener('pointermove', (e) => this.#move(e));
    for (const ev of ['pointerup', 'pointercancel', 'pointerleave']) {
      this.#img.addEventListener(ev, (e) => this.#up(e));
    }
  }

  /* ------------------------------------------------------------- apertura -- */

  open(group, index, fromEl) {
    this.#group = group;
    this.#i = index;
    this.#origin = fromEl;
    this.#closing = false;
    this.#load();
    this.#dlg.showModal();
    document.documentElement.classList.add('lightbox-open');
    this.#animateIn(fromEl);
  }

  #load() {
    const el = this.#group[this.#i];
    if (!this.#img.isConnected) this.#fig.prepend(this.#img);
    this.#reset();
    this.#img.src = el.dataset.full || el.currentSrc || el.src;
    this.#img.alt = el.alt || '';

    // El pie sale del que ya está en la página, salvo cuando no es una frase:
    // el de la foto del hero son dos rótulos («Before», «After») colocados
    // sobre la imagen, y pegados dan «BeforeAfter».
    const zone = el.closest('[data-zoom]');
    const cap = zone?.querySelector('figcaption, .gallery__caption');
    const usable = cap && cap.childElementCount < 2;
    this.#cap.textContent = usable ? cap.textContent.trim() : (el.alt || '');
    this.#cap.hidden = !this.#cap.textContent;

    const many = this.#group.length > 1;
    this.#prev.hidden = this.#next.hidden = !many;
    this.#count.textContent = many ? `${this.#i + 1} / ${this.#group.length}` : '';
    this.#count.hidden = !many;
    this.#apply(true);
  }

  go(step) {
    if (this.#group.length < 2) return;
    this.#i = (this.#i + step + this.#group.length) % this.#group.length;
    this.#origin = this.#group[this.#i];
    this.#load();
  }

  close() {
    if (this.#closing || !this.#dlg.open) return;
    this.#closing = true;
    const back = this.#origin;
    const done = () => {
      if (!this.#closing) return;
      this.#closing = false;
      this.#dlg.close();
      document.documentElement.classList.remove('lightbox-open');
      // El foco vuelve al botón que abrió, no al principio de la página: si no,
      // el usuario de teclado pierde el sitio donde estaba leyendo.
      const btn = back?.closest?.('[data-zoom]')?.querySelector('.zoom-btn');
      (btn || back)?.focus?.({ preventScroll: true });
    };
    if (REDUCED.matches) return done();

    const anim = this.#dlg.animate([{ opacity: 1 }, { opacity: 0 }],
                                   { duration: 160, easing: EASE });
    // El fin de la animación depende del bucle de fotogramas: medido, esos 160 ms
    // tardan entre 234 y 363 en llegar, y con el hilo ocupado decodificando
    // imágenes a veces no llega. Que el cierre cuelgue de eso convierte un
    // adorno en un fallo de accesibilidad — Escape tiene que cerrar siempre.
    const guarda = setTimeout(done, 220);
    const fin = () => { clearTimeout(guarda); done(); };
    anim.finished.then(fin, fin);
  }

  /* Entrada desde el sitio donde estaba la miniatura: la imagen crece desde
     donde el usuario hizo clic, no desde el centro de la nada. */
  #animateIn(fromEl) {
    if (REDUCED.matches || !fromEl) return;
    const from = fromEl.getBoundingClientRect();
    const to = this.#fig.getBoundingClientRect();
    if (!to.width || !to.height || !from.width) return;
    const sx = from.width / to.width;
    const sy = from.height / to.height;
    const dx = from.left + from.width / 2 - (to.left + to.width / 2);
    const dy = from.top + from.height / 2 - (to.top + to.height / 2);
    this.#fig.animate([
      { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, opacity: 0.4 },
      { transform: 'none', opacity: 1 },
    ], { duration: 320, easing: EASE });
    this.#dlg.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: EASE });
  }

  /* --------------------------------------------------------------- gestos -- */

  #down(e) {
    this.#points.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.#img.setPointerCapture(e.pointerId);
    if (this.#points.size === 2) {
      const [a, b] = [...this.#points.values()];
      this.#pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: this.#scale };
      this.#drag = null;
      return;
    }
    if (this.#scale > 1) {
      this.#drag = { x: e.clientX - this.#x, y: e.clientY - this.#y };
      this.#img.classList.add('is-dragging');
    }
  }

  #move(e) {
    if (!this.#points.has(e.pointerId)) return;
    this.#points.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.#pinch && this.#points.size === 2) {
      const [a, b] = [...this.#points.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (this.#pinch.dist > 0) {
        this.#zoomAt(this.#pinch.scale * (dist / this.#pinch.dist),
                     (a.x + b.x) / 2, (a.y + b.y) / 2, true);
      }
      return;
    }
    if (!this.#drag) return;
    this.#x = e.clientX - this.#drag.x;
    this.#y = e.clientY - this.#drag.y;
    this.#clamp();
    this.#apply(true);
  }

  #up(e) {
    this.#points.delete(e.pointerId);
    if (this.#points.size < 2) this.#pinch = null;
    if (this.#points.size === 0) {
      this.#drag = null;
      this.#img.classList.remove('is-dragging');
    }
  }

  /* ----------------------------------------------------------------- zoom -- */

  #reset() { this.#scale = 1; this.#x = 0; this.#y = 0; }

  #zoomCentre(next) {
    const r = this.#img.getBoundingClientRect();
    this.#zoomAt(next, r.left + r.width / 2, r.top + r.height / 2);
  }

  /**
   * Amplía dejando quieto el punto que está bajo el cursor.
   *
   * Con la transformación `translate(x,y) scale(s)`, un punto que ahora se ve a
   * `p` píxeles del centro visible está a `p/s` en la imagen. Para que no se
   * mueva al pasar de `s` a `s'`: x' = x - p·(s'/s - 1). Multiplicar x por el
   * factor, que es el error habitual, dispara la imagen fuera de la pantalla en
   * cuanto haces dos zooms seguidos.
   */
  #zoomAt(next, clientX, clientY, instant = false) {
    const s = Math.max(1, Math.min(MAX, next));
    if (s === this.#scale) return;
    const r = this.#img.getBoundingClientRect();
    const px = clientX - (r.left + r.width / 2);
    const py = clientY - (r.top + r.height / 2);
    const k = s / this.#scale;
    this.#x -= px * (k - 1);
    this.#y -= py * (k - 1);
    this.#scale = s;
    if (s === 1) { this.#x = this.#y = 0; }
    this.#clamp();
    this.#apply(instant);
  }

  /* No dejar que la imagen se salga: al ampliar y arrastrar sin tope se pierde
     de vista y el usuario no sabe cómo volver. Se mide con offsetWidth, que es
     el tamaño de maquetación: getBoundingClientRect ya viene transformado y
     daría un límite que crece con cada zoom. */
  #clamp() {
    const w = this.#img.offsetWidth, h = this.#img.offsetHeight;
    const maxX = Math.max(0, (w * this.#scale - w) / 2);
    const maxY = Math.max(0, (h * this.#scale - h) / 2);
    this.#x = Math.max(-maxX, Math.min(maxX, this.#x));
    this.#y = Math.max(-maxY, Math.min(maxY, this.#y));
  }

  #apply(instant = false) {
    this.#img.style.transition = instant || REDUCED.matches ? 'none' : `transform 240ms ${EASE}`;
    this.#img.style.transform = `translate(${this.#x}px, ${this.#y}px) scale(${this.#scale})`;
    this.#img.dataset.zoomed = this.#scale > 1 ? 'true' : 'false';
    this.#dlg.dataset.zoomed = this.#scale > 1 ? 'true' : 'false';
  }
}

/* ---------------------------------------------------------------- enganche */

const ICON = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
  stroke-width="1.6" stroke-linecap="round" aria-hidden="true">
  <circle cx="10.5" cy="10.5" r="6.5"/><path d="M20 20l-4.7-4.7M10.5 7.6v5.8M7.6 10.5h5.8"/></svg>`;

let box = null;

function boot() {
  const zones = [...document.querySelectorAll('[data-zoom]')];
  if (!zones.length) return;
  box ??= new Lightbox();

  // Los contenedores que comparten valor de data-zoom se recorren con flechas
  const groups = new Map();
  for (const z of zones) {
    const key = z.dataset.zoom || `#${zones.indexOf(z)}`;
    (groups.get(key) ?? groups.set(key, []).get(key)).push(...z.querySelectorAll('img'));
  }

  for (const zone of zones) {
    if (zone.dataset.zoomBound) continue;
    zone.dataset.zoomBound = '1';

    const group = groups.get(zone.dataset.zoom || `#${zones.indexOf(zone)}`);
    const imgs = [...zone.querySelectorAll('img')];
    if (!imgs.length) continue;

    // El botón necesita un ancestro posicionado. Se toca solo si hace falta:
    // .buy__media es sticky y forzarle relative le rompería el comportamiento.
    // OJO: pasar de `static` a `relative` despierta cualquier `top/left/inset`
    // que la hoja de estilos hubiera dejado suelto —en `static` son inertes—.
    // Ya mordió una vez: la foto del bloque de compra bajaba 88 px en móvil por
    // un `top: 5.5rem` sobrante del `sticky` de escritorio. Si vuelve a pasar,
    // el arreglo va en el CSS de la zona, no aquí.
    if (getComputedStyle(zone).position === 'static') zone.style.position = 'relative';

    for (const img of imgs) {
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', () => box.open(group, group.indexOf(img), img));
    }

    // Un botón de verdad, visible y en una esquina: con teclado se llega, y en
    // móvil se ve — el aviso al pasar el ratón allí no existe.
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'zoom-btn';
    btn.innerHTML = `${ICON}<span>Enlarge</span>`;
    btn.setAttribute('aria-label', `Enlarge image: ${imgs[0].alt || 'product photo'}`);
    btn.addEventListener('click', () => box.open(group, group.indexOf(imgs[0]), imgs[0]));
    zone.append(btn);
  }
}

document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', boot)
  : boot();
document.addEventListener('shopify:section:load', boot);
